/**
 * Documents Tab - Lists, uploads, downloads and deletes documents for a supplier.
 * Supports importance filter, folder filter, list/tiles view, drag-drop upload.
 * Terminology matches the side-menu Documents page: the DocumentCategory
 * entity is a "Folder" to users; this tab used to call it a "Category".
 */

import { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsCustomer } from '../../hooks/usePortalMode';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Loader2, FileText, Upload, Search, LayoutGrid, List, Filter, ShieldCheck,
} from 'lucide-react';
import {
  documentsAPI, type SupplierProfile, type Document as DocType, type DocumentCategory,
} from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { useConfirm } from '../../contexts/ConfirmDialogContext';
import { toast } from 'sonner';
import { DocumentDetailModal } from './DocumentDetailModal';
import { DocTilesView, SupplierDocBadges } from './DocumentListViews';
import { DocumentRowList, DocumentSortHeader, useDocumentSort } from '../document-manager/DocumentRows';
import FileDropZone from '../../components/common/FileDropZone';

// Same list the tab's picker has always offered.
const SUPPLIER_DOC_ACCEPT = '.png,.jpg,.jpeg,.bmp,.doc,.docx,.xls,.xlsx,.pdf,.ppt,.pptx,.txt,.svg,.csv,.zip,.rar,.7z';

interface DocumentsTabProps {
  supplier: SupplierProfile;
}

type ViewMode = 'list' | 'tiles';
type ImportanceFilter = 'all' | 'low' | 'medium' | 'high' | 'critical';


export function DocumentsTab({ supplier }: DocumentsTabProps) {
  const { formatDate } = useFormatters();
  const confirm = useConfirm();
  const navigate = useNavigate();
  // Delete is gated on isCustomer, matching the side-menu Documents page. This
  // tab previously rendered the delete button to anyone who could reach the
  // supplier record.
  const isCustomer = useIsCustomer();

  /** Open the side-menu Documents page drilled into a folder. Same ?folder=
   *  param the Documents page already uses for its own breadcrumb state, so
   *  the two surfaces share one navigation contract. */
  const openFolderInDocuments = (categoryId: number) => {
    navigate(`/documents?folder=${categoryId}`);
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  // Synchronous re-entry lock — `disabled={uploading}` only applies after a
  // re-render, so without this a double-click on a slow upload posts the
  // files twice. See the same guard in useDocuments.handleUploadSubmit.
  const uploadInFlightRef = useRef(false);

  const [documents, setDocuments] = useState<DocType[]>([]);
  const [categories, setCategories] = useState<DocumentCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [detailDoc, setDetailDoc] = useState<DocType | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [importanceFilter, setImportanceFilter] = useState<ImportanceFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  // CR-5858: queued files awaiting metadata confirmation before upload.
  const [pendingUpload, setPendingUpload] = useState<File[] | null>(null);
  const [requireCompanyConfirmation, setRequireCompanyConfirmation] = useState(false);

  useEffect(() => {
    fetchDocuments();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplier.company]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      // Walk all pages. A bare getDocuments() returns only the first 20, which
      // both truncated this list and silently dropped folders from the picker
      // below (relevantFolders is derived from these rows).
      // for_company, not company: `company` matches only documents FILED
      // AGAINST this supplier, so everything a customer uploaded and shared
      // DOWN to them (company=<customer>, supplier in shared_with_companies)
      // was missing from the very tab meant to show it.
      setDocuments(await documentsAPI.getAllDocuments({
        for_company: supplier.company, ordering: '-version_uploaded_at',
      }));
    } catch (err: unknown) {
      console.error('Failed to fetch documents:', err);
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      // Walk all pages — a bare getCategories() returned only the first 20,
      // so the folder filter silently omitted folders beyond that.
      setCategories(await documentsAPI.getAllCategories());
    } catch (err: unknown) {
      console.error('Failed to fetch folders:', err);
    }
  };

  // CR-5858: file selection no longer uploads directly — it stages files
  // in a metadata dialog where the customer can flip the
  // "Read & Understood" checkbox before submitting.
  // Customer review 11 Sep (drag and drop everywhere): the header Upload
  // button and the drop zone share one input, owned by FileDropZone.
  const queueUpload = (files: File[]) => {
    if (files.length === 0) return;
    setPendingUpload(files);
    setRequireCompanyConfirmation(false);
  };

  const cancelUpload = () => {
    setPendingUpload(null);
    setRequireCompanyConfirmation(false);
  };

  const handleUpload = async () => {
    if (!pendingUpload || pendingUpload.length === 0) return;
    if (uploadInFlightRef.current) return;
    uploadInFlightRef.current = true;
    setUploading(true);
    let successCount = 0;
    for (let i = 0; i < pendingUpload.length; i++) {
      const file = pendingUpload[i];
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('title', file.name);
        formData.append('company', String(supplier.company));
        formData.append('document_type', 'file');
        formData.append('is_public', 'false');
        if (requireCompanyConfirmation) {
          formData.append('requires_company_confirmation', 'true');
        }
        await documentsAPI.createDocument(formData);
        successCount++;
      } catch (err: unknown) {
        console.error(`Failed to upload ${file.name}:`, err);
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    if (successCount > 0) {
      toast.success(`${successCount} document${successCount > 1 ? 's' : ''} uploaded`);
      await fetchDocuments();
    }
    setUploading(false);
    uploadInFlightRef.current = false;
    setPendingUpload(null);
    setRequireCompanyConfirmation(false);
  };

  const handleDelete = async (doc: DocType) => {
    const ok = await confirm({
      title: 'Delete Document',
      description: `Are you sure you want to delete "${doc.title}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      setDeletingId(doc.id);
      await documentsAPI.deleteDocument(doc.id);
      toast.success('Document deleted');
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
    } catch (err: unknown) {
      console.error('Failed to delete document:', err);
      toast.error('Failed to delete document');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownload = async (doc: DocType) => {
    try {
      const res = await documentsAPI.downloadDocument(doc.id);
      if (res.data.file_url) window.open(res.data.file_url, '_blank');
    } catch (err: unknown) {
      console.error('Failed to download document:', err);
      toast.error('Failed to download document');
    }
  };

  // Apply filters
  const filtered = documents.filter((d) => {
    if (search) {
      const q = search.toLowerCase();
      if (!d.title.toLowerCase().includes(q) &&
          !(d.description || '').toLowerCase().includes(q) &&
          !(d.tags || '').toLowerCase().includes(q)) return false;
    }
    if (importanceFilter !== 'all' && d.importance !== importanceFilter) return false;
    if (categoryFilter !== 'all') {
      const catId = parseInt(categoryFilter, 10);
      if (d.category !== catId) return false;
    }
    return true;
  });

  // Folders that actually contain one of this supplier's documents. The tab
  // previously listed every folder on the install, most of which could never
  // match a row here — and since uploads from this tab set no folder at all,
  // the picker was mostly decorative.
  const relevantFolders = useMemo(() => {
    const present = new Set(documents.map((d) => d.category).filter((c): c is number => c != null));
    return categories.filter((c) => c.id != null && present.has(c.id));
  }, [documents, categories]);

  const hasActiveFilters = importanceFilter !== 'all' || categoryFilter !== 'all';

  // Same Name / Date Uploaded sort as the Documents page list (newest first).
  const { sortKey, sortDir, toggleSort, sorted } = useDocumentSort(filtered);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Documents
            {!loading && <Badge variant="outline" className="ml-2">{documents.length}</Badge>}
          </CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-40" />
            </div>
            <Button variant={showFilters ? 'secondary' : 'outline'} size="sm" onClick={() => setShowFilters(!showFilters)}>
              <Filter className="h-4 w-4 mr-1" />
              Filters{hasActiveFilters ? ' *' : ''}
            </Button>
            <div className="flex border rounded-md">
              <Button variant={viewMode === 'list' ? 'secondary' : 'ghost'} size="icon" className="h-9 w-9 rounded-r-none" onClick={() => setViewMode('list')} title="List"><List className="h-4 w-4" /></Button>
              <Button variant={viewMode === 'tiles' ? 'secondary' : 'ghost'} size="icon" className="h-9 w-9 rounded-l-none" onClick={() => setViewMode('tiles')} title="Tiles"><LayoutGrid className="h-4 w-4" /></Button>
            </div>
            <Button size="sm" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
              {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}Upload
            </Button>
          </div>
        </div>
        {/* Filter row */}
        {showFilters && (
          <div className="flex gap-3 mt-3 flex-wrap">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Importance</label>
              <select value={importanceFilter} onChange={(e) => setImportanceFilter(e.target.value as ImportanceFilter)} className="text-sm border rounded-md px-2 py-1.5 bg-background">
                <option value="all">All</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Folder</label>
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="text-sm border rounded-md px-2 py-1.5 bg-background">
                <option value="all">All</option>
                {relevantFolders.map((cat) => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
              </select>
            </div>
            {hasActiveFilters && (
              <div className="flex items-end">
                <Button variant="ghost" size="sm" onClick={() => { setImportanceFilter('all'); setCategoryFilter('all'); }}>Clear</Button>
              </div>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent>
        <FileDropZone
          multiple
          accept={SUPPLIER_DOC_ACCEPT}
          busy={uploading}
          inputRef={fileInputRef}
          className="mb-4"
          hint="Images, PDF, Word, Excel, PowerPoint, text, CSV or archives"
          onFiles={queueUpload}
          data-testid="supplier-documents-drop-zone"
        />

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>
        ) : error ? (
          <div className="text-center py-12 text-red-600"><p>{error}</p>
            <button onClick={fetchDocuments} className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90">Retry</button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>{search || hasActiveFilters ? 'No documents match your filters.' : 'No documents uploaded for this supplier yet.'}</p>
          </div>
        ) : viewMode === 'list' ? (
          // The Documents page's list: sortable Name / Date Uploaded columns.
          <div className="space-y-1">
            <DocumentSortHeader sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            <DocumentRowList
              documents={sorted}
              onDownload={handleDownload}
              onDelete={isCustomer ? handleDelete : undefined}
              onOpenDetail={setDetailDoc}
              renderBadges={(doc) => <SupplierDocBadges doc={doc} onOpenFolder={openFolderInDocuments} />}
            />
          </div>
        ) : (
          <DocTilesView docs={sorted} formatDate={formatDate} deletingId={deletingId} onDownload={handleDownload} onDelete={isCustomer ? handleDelete : undefined} onOpenDetail={setDetailDoc} onOpenFolder={openFolderInDocuments} />
        )}
      </CardContent>

      {/* Document Detail Modal */}
      {detailDoc && (
        <DocumentDetailModal
          document={detailDoc}
          categories={categories}
          onClose={() => setDetailDoc(null)}
          onUpdated={(updated) => {
            setDocuments((prev) => prev.map((d) => d.id === updated.id ? updated : d));
            setDetailDoc(updated);
          }}
        />
      )}

      {/* CR-5858: Upload metadata dialog — surfaces the
          "Require Read & Understood Confirmation" checkbox before files
          are submitted. */}
      <Dialog open={pendingUpload !== null} onOpenChange={(o) => { if (!o && !uploading) cancelUpload(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload documents</DialogTitle>
            <DialogDescription>
              {pendingUpload && pendingUpload.length === 1
                ? `Upload "${pendingUpload[0].name}" for this supplier.`
                : `Upload ${pendingUpload?.length ?? 0} documents for this supplier.`}
            </DialogDescription>
          </DialogHeader>

          <label className="flex items-start gap-3 cursor-pointer rounded-md border p-3 hover:bg-muted/30">
            <Checkbox
              id="require-company-confirmation"
              checked={requireCompanyConfirmation}
              onCheckedChange={(v) => setRequireCompanyConfirmation(v === true)}
              className="mt-0.5"
            />
            <div>
              <p className="text-sm font-medium flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4 text-amber-600" />
                Require Read &amp; Understood Confirmation
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                The supplier company must confirm before continuing to use the portal.
                A new document version resets the confirmation status and requires a fresh acknowledgement.
              </p>
            </div>
          </label>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={cancelUpload} disabled={uploading}>
              Cancel
            </Button>
            <Button type="button" onClick={handleUpload} disabled={uploading}>
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ── Sub-components ─────────────────────────────────────────── */
